<script setup lang="ts">
import type { SkillCard } from '~/composables/useApi'

/**
 * スキル獲得のお知らせの本文（#966。イベントの speaker: skills）。
 *
 * 本文（`ids`）はサーバのイベントが集めた「新しく覚えたもの」の ID（`<skill>` / `<skill>.<key>`、「,」区切り）。
 * 名前・カテゴリーはスキル一覧と同じ API（`listSkills`）から引き、**一覧の左の列と同じカード**で並べる
 * （2026-10-07 doi: 汎用の本文ではワクワクしない）。カードは 1 枚ずつ順に現れる。
 */
const props = defineProps<{ ids: string }>()

const api = useApi()
const skills = ref<SkillCard[]>([])
onMounted(async () => {
  try {
    skills.value = (await api.listSkills()).skills
  }
  catch (e) {
    console.error('[SkillLearnedBody] failed to load skills', e)
  }
})

const wanted = computed(() => new Set(props.ids.split(',').map(s => s.trim()).filter(Boolean)))

/** 一覧の左の列と同じ見出し・同じ並び */
const KIND_TITLE: Record<SkillCard['kind'], string> = {
  pyxel: 'Pyxel の基本',
  grammar: 'Python の書き方',
  technique: 'ゲーム開発テクニック',
  genre: 'ジャンル',
}
const groups = computed(() => {
  const byKind = (kind: SkillCard['kind']) => skills.value
    .filter(s => s.kind === kind && s.skillId)
    .flatMap(s => s.types.length
      ? s.types.filter(t => t.key && wanted.value.has(`${s.skillId}.${t.key}`)).map(t => ({ id: `${s.skillId}.${t.key}`, label: t.label ?? '' }))
      : (wanted.value.has(s.skillId!) ? [{ id: s.skillId!, label: s.name ?? '' }] : []))
  return (Object.keys(KIND_TITLE) as SkillCard['kind'][])
    .map(kind => ({ title: KIND_TITLE[kind], items: byKind(kind) }))
    .filter(g => g.items.length)
})

/** 何枚目か（全体の通し番号）。現れる順の遅れに使う */
const order = computed(() => {
  const out = new Map<string, number>()
  groups.value.flatMap(g => g.items).forEach((item, i) => out.set(item.id, i))
  return out
})
</script>

<template>
  <div
    class="skill-learned"
    data-testid="skill-learned"
  >
    <!-- 詳細はスキル一覧で（2026-10-07 doi: 1 行で）。スクロールしても見えるよう、カードの上に置く -->
    <p class="skill-learned__lead">
      覚えたスキルは、右上のメニューの「スキル」で見られます。
    </p>
    <section
      v-for="group in groups"
      :key="group.title"
      class="skill-learned__group"
    >
      <h3 class="skill-learned__title">
        {{ group.title }}
      </h3>
      <div class="skill-learned__items">
        <SkillChip
          v-for="item in group.items"
          :key="item.id"
          :label="item.label"
          class="skill-learned__chip"
          :style="{ animationDelay: `${Math.min(order.get(item.id) ?? 0, 30) * 70}ms` }"
          data-testid="skill-learned-chip"
        />
      </div>
    </section>
  </div>
</template>

<style scoped>
.skill-learned {
  display: flex;
  flex-direction: column;
  gap: max(10px, calc(1.2 * var(--u)));
  font-weight: normal;
}

.skill-learned__lead {
  font-size: max(11px, calc(1.3 * var(--u)));
  opacity: 0.75;
  white-space: nowrap;
}

.skill-learned__title {
  font-size: max(12px, calc(1.4 * var(--u)));
  font-weight: bold;
  margin-bottom: max(4px, calc(0.5 * var(--u)));
  opacity: 0.8;
}

.skill-learned__items {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: max(6px, calc(0.7 * var(--u)));
}

/* 1 枚ずつ、ぽんと現れる（覚えた喜びの演出。2026-10-07 doi） */
.skill-learned__chip {
  animation: skill-learned-pop 0.42s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes skill-learned-pop {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.8);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skill-learned__chip {
    animation: none;
  }
}
</style>
